<!-- =========================================================================================
  File Name: ClusterInfoWindow.vue
  Description: Info window popup for cluster points
  ----------------------------------------------------------------------------------------
  Author: Serviroutes Team
  Version: 2025-10-23
========================================================================================== -->

<template>
	<div>
		<div class="cluster-info-window" :style="windowStyle">
			<div class="info-window-content">
				<!-- Cluster Summary Information (when clicked on polygon) -->
				<!-- Header with Close Button (Territory Manager Style) -->
				<div class="info-header">
					<div class="header-left">
						<div
							v-if="isClusterSummary"
							class="cluster-badge"
							:style="{backgroundColor: data.point.cluster_color}"
						></div>
						<h6 v-if="isClusterSummary" class="info-title">
							{{
								viewMode === "micro_clusters"
									? $t("Clustering.MicroClusterInfo")
									: $t("Clustering.ClusterInfo")
							}}
						</h6>
						<h6 v-else class="info-title">{{ $t("VisitPoint") }}</h6>
					</div>
					<button class="close-btn-territory" @click="$emit('close')">X</button>
				</div>

				<vs-divider v-if="isClusterSummary" class="my-2"></vs-divider>

				<!-- Cluster Information -->
				<div class="info-body">
					<template v-if="isClusterSummary">
						<!-- Show different header for cluster polygon vs individual point -->
						<div v-if="isClusterSummary" class="cluster-summary-header">
							<div class="info-row highlight">
								<span class="info-label">
									<feather-icon icon="LayersIcon" svgClasses="w-3 h-3" />
									{{
										viewMode === "micro_clusters"
											? $t("Clustering.MicroCluster")
											: $t("Clustering.Cluster")
									}}:
								</span>
								<span class="info-value bold">{{ data.point.identification }}</span>
							</div>
						</div>
						<div v-else>
							<div class="info-row">
								<span class="info-label">
									<feather-icon icon="LayersIcon" svgClasses="w-3 h-3" />
									{{ $t("Clustering.ClusterId") }}:
								</span>
								<span class="info-value">{{ data.point.cluster_identification }}</span>
							</div>

							<!-- MicroCluster Information (if viewMode is micro_clusters) -->
							<div v-if="viewMode === 'micro_clusters' && microClusterId" class="info-row">
								<span class="info-label">
									<feather-icon icon="GitBranchIcon" svgClasses="w-3 h-3" />
									{{ $t("Clustering.MicroClusterId") }}:
								</span>
								<span class="info-value">{{ microClusterId }}</span>
							</div>
						</div>
						<vs-divider class="my-2"></vs-divider>

						<h6 class="section-title">{{ $t("Clustering.ClusterSummary") }}</h6>

						<div class="info-row">
							<span class="info-label">
								<feather-icon icon="UsersIcon" svgClasses="w-3 h-3" />
								{{ $t("Clustering.TotalPoints") }}:
							</span>
							<span class="info-value bold">{{ data.point.totalPoints }}</span>
						</div>

						<div class="info-row">
							<span class="info-label">
								<feather-icon icon="PackageIcon" svgClasses="w-3 h-3" />
								{{ $t("Clustering.TotalDemand") }}:
							</span>
							<span class="info-value">{{ formatNumber(pointDemand) }}</span>
						</div>

						<div class="info-row">
							<span class="info-label">
								<feather-icon icon="TrendingUpIcon" svgClasses="w-3 h-3" />
								{{ $t("Clustering.AvgDemand") }}:
							</span>
							<span class="info-value">{{ formatNumber(data.point.avgDemand) }}</span>
						</div>

						<div class="info-row">
							<span class="info-label">
								<feather-icon icon="TargetIcon" svgClasses="w-3 h-3" />
								{{ $t("Clustering.Center") }}:
							</span>
							<span class="info-value coordinates">
								{{ formatCoordinate(data.point.cy || data.point.latitude) }},
								{{ formatCoordinate(data.point.cx || data.point.longitude) }}
							</span>
						</div>
					</template>

					<!-- Individual Point Information -->
					<template v-else-if="data.point">
						<vs-divider class="my-2"></vs-divider>

						<h6 v-if="isClusterSummary" class="section-title">{{ $t("Clustering.PointDetails") }}</h6>

						<div class="info-row">
							<span class="info-label">
								<feather-icon icon="HashIcon" svgClasses="w-3 h-3" />
								{{ $t("Routing.Table.Identification") }}:
							</span>
							<span class="info-value">{{ data.point.identification || "-" }}</span>
						</div>

						<div class="info-row">
							<span class="info-label">
								<feather-icon icon="NavigationIcon" svgClasses="w-3 h-3" />
								{{ $t("Routing.Map.Coordinates") }}:
							</span>
							<span class="info-value coordinates">
								{{ formatCoordinate(data.point.cy || data.point.latitude) }},
								{{ formatCoordinate(data.point.cx || data.point.longitude) }}
							</span>
						</div>

						<!-- Demand Information -->
						<div v-if="pointDemand > 0" class="info-row">
							<span class="info-label">
								<feather-icon icon="PackageIcon" svgClasses="w-3 h-3" />
								{{ $t("Routing.Table.Demand") }}:
							</span>
							<span class="info-value">{{ pointDemand }}</span>
						</div>
						<div v-if="data.point.cluster_identification || microClusterId" class="info-row">
							<span class="info-label">
								<feather-icon icon="LayersIcon" svgClasses="w-3 h-3" />
								{{ $t("Clustering.ClusterId") }}:
							</span>
							<span class="info-value">{{ data.point.cluster_identification }}</span>
						</div>
						<!-- MicroCluster Information (if viewMode is micro_clusters) -->
						<div v-if="viewMode === 'micro_clusters' && microClusterId" class="info-row">
							<span class="info-label">
								<feather-icon icon="GitBranchIcon" svgClasses="w-3 h-3" />
								{{ $t("Clustering.MicroClusterId") }}:
							</span>
							<span class="info-value">{{ microClusterId }}</span>
						</div>
					</template>
				</div>
				<div class="info-footer">
					<!-- StreetView - only for individual points -->
					<div v-if="!isClusterSummary && showStreetView" class="streetview-container">
						<img
							:src="require('@/assets/images/maps/google-street-view-icon-5.png')"
							alt="StreetView"
							title="Ver en StreetView"
							class="streetview-img"
							@click="openStreetView"
						/>
					</div>

					<!-- Action Buttons -->
					<div class="action-buttons">
						<!-- Buttons for cluster/microcluster polygon -->
						<template v-if="isClusterSummary">
							<vs-button
								class="half-width"
								color="primary"
								type="filled"
								icon-pack="feather"
								icon="icon-zoom-in"
								size="small"
								@click="zoomToCluster"
							>
								{{ $t("Clustering.ZoomTo") }}
							</vs-button>
							<vs-button
								v-if="isFocused"
								class="half-width"
								color="danger"
								type="filled"
								icon-pack="feather"
								icon="icon-x-circle"
								size="small"
								@click="removeFocusOnCluster"
							>
								{{ $t("Clustering.RemoveFocus") }}
							</vs-button>
							<vs-button
								v-else
								class="half-width"
								color="warning"
								type="filled"
								icon-pack="feather"
								icon="icon-eye"
								size="small"
								@click="focusOnCluster"
							>
								{{ $t("Clustering.FocusOn") }}
							</vs-button>
						</template>

						<!-- Button for individual point -->
						<vs-button
							v-else
							class="w-full"
							color="primary"
							type="border"
							icon-pack="feather"
							icon="icon-zoom-in"
							size="small"
							@click="zoomToPoint"
						>
							{{ $t("Clustering.ZoomToPoint") }}
						</vs-button>
					</div>
				</div>
			</div>
		</div>
		<vs-popup title="Google Street View" :active.sync="popupStreetViewActive" style="z-index: 99999">
			<div id="gpanorama" class="street-view-container"></div>
		</vs-popup>
	</div>
</template>

<script>
export default {
	name: "ClusterInfoWindow",
	props: {
		data: {
			type: Object,
			required: true,
		},
		position: {
			type: Object,
			default: null,
		},
		clusterStats: {
			type: Object,
			default: null,
		},
		viewMode: {
			type: String,
			default: "clusters",
		},
		showStreetView: {
			type: Boolean,
			default: true,
		},
		clustering: {
			type: Object,
			default: null,
		},
		isFocused: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			popupStreetViewActive: false,
		};
	},
	computed: {
		windowStyle() {
			if (!this.position) {
				return {
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
				};
			}

			return {
				top: `${this.position.y + 10}px`,
				left: `${this.position.x + 10}px`,
			};
		},
		clusterId() {
			if (this.data.clusterId === null || this.data.clusterId === undefined) {
				return "-";
			}
			return `Cluster ${parseInt(this.data.clusterId) + 1}`;
		},
		microClusterId() {
			if (!this.data.point || !this.data.point.micro_cluster_id) {
				return null;
			}
			return this.data.point.micro_cluster_id;
		},
		pointDemand() {
			if (!this.data.point || !this.data.point.demand) {
				return 0;
			}
			return this.data.point.demand;
		},
		isClusterSummary() {
			return this.data.point && this.data.point.isClusterSummary === true;
		},
		clusterIdForActions() {
			if (this.viewMode === "micro_clusters") {
				return this.data.point.micro_cluster_id;
			}
			return this.data.point.cluster_identification;
		},
	},
	methods: {
		formatCoordinate(value) {
			if (!value) return "0.000000";
			return parseFloat(value).toFixed(6);
		},
		formatNumber(value) {
			if (!value) return "0";
			return parseFloat(value).toFixed(2);
		},
		zoomToPoint() {
			this.$emit("zoom-to-point", this.data.point);
			this.$emit("close");
		},
		zoomToCluster() {
			this.$emit("zoom-to-cluster", this.clusterIdForActions);
			this.$emit("close");
		},
		focusOnCluster() {
			this.$emit("focus-on-cluster", this.clusterIdForActions);
			this.$emit("close");
		},
		removeFocusOnCluster() {
			this.$emit("clear-focus");
			this.$emit("close");
		},
		openStreetView() {
			if (!this.data.point) return;
			const lat = this.data.point.cy || this.data.point.latitude;
			const lng = this.data.point.cx || this.data.point.longitude;
			if (!lat || !lng) return;

			// Abrir el popup
			this.popupStreetViewActive = true;

			// Esperar a que el popup se monte
			this.$nextTick(() => {
				this.initStreetView(lat, lng);
			});
		},
		initStreetView(lat, lng) {
			const panorama = new google.maps.StreetViewPanorama(document.getElementById("gpanorama"), {
				position: {lat: parseFloat(lat), lng: parseFloat(lng)},
				pov: {heading: 34, pitch: 10},
				zoom: 1,
			});
			return panorama;
		},
	},
};
</script>

<style lang="scss" scoped>
.cluster-info-window {
	position: fixed;
	z-index: 1000;
	min-width: 320px;
	max-width: 420px;
	background: white;
	border-radius: 8px;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
	animation: fadeInScale 0.3s ease-out;
	pointer-events: auto;

	.info-window-content {
		padding: 1rem;

		.info-header {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 0.5rem;

			.header-left {
				display: flex;
				align-items: center;
				gap: 0.5rem;
				flex: 1;

				.cluster-badge {
					width: 14px;
					height: 14px;
					border-radius: 50%;
					box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
					flex-shrink: 0;
				}

				.info-title {
					font-size: 0.95rem;
					font-weight: 600;
					margin: 0;
				}
			}

			.close-btn-territory {
				background-color: #ff9800;
				color: white;
				border: none;
				border-radius: 50%;
				width: 28px;
				height: 28px;
				display: flex;
				align-items: center;
				justify-content: center;
				cursor: pointer;
				font-weight: bold;
				font-size: 14px;
				transition: all 0.2s ease;
				flex-shrink: 0;

				&:hover {
					background-color: #f57c00;
					transform: scale(1.1);
				}

				&:active {
					transform: scale(0.95);
				}
			}
		}

		.info-body {
			.cluster-summary-header {
				background: linear-gradient(135deg, rgba(var(--vs-primary), 0.6) 0%, rgba(var(--vs-primary), 1) 100%);
				margin: -0.5rem -0.5rem 0.5rem -0.5rem;
				padding: 0.75rem;
				border-radius: 6px;

				.info-row.highlight {
					margin-bottom: 0;

					.info-label,
					.info-value {
						color: white;
					}

					.info-value.bold {
						font-weight: 700;
						font-size: 1rem;
					}
				}
			}

			.section-title {
				font-size: 0.85rem;
				font-weight: 600;
				margin-bottom: 0.5rem;
				color: #555;
			}

			.info-row {
				display: flex;
				justify-content: space-between;
				align-items: flex-start;
				margin-bottom: 0.5rem;
				font-size: 0.85rem;

				.info-label {
					display: flex;
					align-items: center;
					gap: 0.25rem;
					font-weight: 500;
					color: #666;
					flex-shrink: 0;
					margin-right: 0.5rem;
				}

				.info-value {
					font-weight: 400;
					color: #333;
					text-align: right;
					word-break: break-word;

					&.coordinates {
						font-family: monospace;
						font-size: 0.8rem;
					}

					&.bold {
						font-weight: 700;
						color: #1a1a1a;
					}
				}
			}
		}

		.info-footer {
			margin-top: 0.75rem;

			.streetview-container {
				display: flex;
				align-items: center;
				justify-content: flex-end;
				gap: 0.5rem;
				margin-bottom: 0.75rem;

				.streetview-img {
					width: 30px;
					height: 30px;
					border-radius: 4px;
					cursor: pointer;
					transition: all 0.2s ease;
					box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

					&:hover {
						transform: scale(1.05);
						box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
					}
				}

				.detail-btn {
					display: flex;
					align-items: center;
					padding: 8px;
					background-color: #ff9800;
					color: white;
					border: none;
					cursor: pointer;
					border-radius: 5px;
					transition: all 0.2s ease;

					&:hover {
						background-color: #f57c00;
						transform: scale(1.05);
					}

					&:active {
						transform: scale(0.95);
					}
				}
			}

			.action-buttons {
				display: flex;
				gap: 0.5rem;

				.half-width {
					flex: 1;
				}
			}
		}
	}
}
.street-view-container {
	width: 100%;
	height: 500px;
	min-height: 500px;
}

@keyframes fadeInScale {
	from {
		opacity: 0;
		transform: scale(0.9);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

// Responsive
@media screen and (max-width: 480px) {
	.cluster-info-window {
		min-width: 250px;
		max-width: calc(100vw - 40px);

		.info-window-content {
			.info-body {
				.info-row {
					flex-direction: column;
					gap: 0.25rem;

					.info-value {
						text-align: left;
					}
				}
			}
		}
	}
}
</style>
